import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform, useReducedMotion } from "framer-motion";

const GLYPHS = "█▓▒░<>/|=+*01∆◊";
const DECODE_FRAMES = 24;

// Scrambles from random data-glyphs into the real label as the title enters
// the viewport, echoing the glyph rings orbiting the video figure.
function useDecodedText(text, play) {
  const [display, setDisplay] = useState(text);

  // Replays the scramble on every `play` rising edge (each time the title
  // re-enters the viewport, scrolling down or back up).
  useEffect(() => {
    if (!play) return;
    let frame = 0;
    const id = setInterval(() => {
      frame += 1;
      const resolved = Math.floor((frame / DECODE_FRAMES) * text.length);
      let out = "";
      for (let i = 0; i < text.length; i += 1) {
        if (i < resolved || text[i] === " ") out += text[i];
        else out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      }
      setDisplay(frame >= DECODE_FRAMES ? text : out);
      if (frame >= DECODE_FRAMES) clearInterval(id);
    }, 35);
    return () => clearInterval(id);
  }, [play, text]);

  return display;
}

// Sparse section heading tuned for the video background: thin, spaced-out
// type that decodes on entry and drifts slower than the page (parallax).
const SectionTitle = ({ children }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { margin: "-10% 0px" });
  const label = typeof children === "string" ? children : null;
  const decoded = useDecodedText(label ?? "", Boolean(label) && inView && !reduced);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [36, -36]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

  return (
    <motion.h1
      ref={ref}
      style={reduced ? undefined : { y, opacity }}
      className="text-glow mb-14 text-center text-4xl font-light uppercase tracking-[0.25em] text-white"
    >
      {label ? (reduced || !inView ? label : decoded) : children}
    </motion.h1>
  );
};

export default SectionTitle;
